import * as React from 'react';
import { useEffect, useLayoutEffect, useRef } from 'react';

/** Material UI Packages. */
import { TextField } from '@mui/material';
import { useGridApiContext } from '@mui/x-data-grid-pro';

/**
 * Note 셀을 Enter 로 열 때 맨 앞에 빈 줄이 들어가는 것을 막는다.
 * MUI 는 Enter 로 편집을 "시작"만 하고 그 키의 기본 동작은 막지 않는데, 그 사이 아래 textarea 가
 * 포커스를 가져가 브라우저가 같은 Enter 를 줄바꿈 입력으로 처리해 버리기 때문이다.
 * 기본 동작만 취소하므로 편집 모드는 그대로 열린다. 그리드의 `onCellKeyDown` 에서 호출한다.
 */
export const preventNoteEnterLineBreak = (params, event) => {
    if (event.key === 'Enter' && params.field === 'note' && params.cellMode === 'view') {
        event.preventDefault();
    }
};

/**
 * DataGrid 여러 줄 Note 편집 셀. 엑셀과 같이 Enter 는 저장(편집 종료), Alt+Enter 는 줄바꿈이다.
 * 셀 밖을 클릭해도 커밋되고, 편집을 열면 커서는 내용 맨 끝에 놓인다.
 * 안쪽 여백은 0 으로 고정한다 — 여백은 셀에서 주어야 보기 상태와 편집 상태의 높이가 같아진다.
 * 글자 크기 같은 화면별 값은 `sx` 로 넘긴다(기본 sx 와 배열로 합쳐져 겹치는 속성만 덮어쓴다).
 */
export default function MultilineNoteEditCell({ params, sx }) {
    const apiRef = useGridApiContext();
    const { id, field, value } = params;
    const inputRef = useRef(null);
    // Alt+Enter 로 줄바꿈을 넣은 뒤 커서를 줄바꿈 바로 다음에 되돌려 놓기 위한 위치.
    const pendingCaretRef = useRef(null);

    /** 편집을 열면 커서를 내용 맨 끝으로 보낸다 — textarea 는 포커스를 받으면 기본적으로 맨 앞에 선다. */
    useLayoutEffect(() => {
        const input = inputRef.current;
        if (!input) return;
        const end = input.value.length;
        input.setSelectionRange(end, end);
    }, []);

    useEffect(() => {
        if (pendingCaretRef.current == null || !inputRef.current) return;
        const caret = pendingCaretRef.current;
        pendingCaretRef.current = null;
        inputRef.current.setSelectionRange(caret, caret);
    }, [value]);

    return (
        <TextField
            value={value ?? ''}
            inputRef={inputRef}
            onChange={(event) => apiRef.current.setEditCellValue({ id, field, value: event.target.value })}
            onKeyDown={(event) => {
                const isClipboardKey =
                    (event.ctrlKey || event.metaKey) && ['c', 'v', 'x'].includes(event.key.toLowerCase());
                if (isClipboardKey) {
                    event.stopPropagation();
                    return;
                }
                if (event.key !== 'Enter') return;
                if (!event.altKey) {
                    event.preventDefault();
                    return;
                }
                event.preventDefault();
                event.stopPropagation();
                const input = event.target;
                const current = value ?? '';
                const start = input.selectionStart ?? current.length;
                const end = input.selectionEnd ?? start;
                pendingCaretRef.current = start + 1;
                apiRef.current.setEditCellValue({
                    id,
                    field,
                    value: `${current.slice(0, start)}\n${current.slice(end)}`,
                });
            }}
            autoFocus
            multiline
            fullWidth
            variant="standard"
            inputProps={{ maxLength: 255 }}
            sx={[
                {
                    height: '100%',
                    // MUI 가 multiline 에 기본으로 넣는 안쪽 여백(4px/5px)을 없앤다.
                    '& .MuiInputBase-root': { p: 0 },
                    '& .MuiInputBase-input': { p: 0, lineHeight: 1.4 },
                    '& .MuiInput-underline:before, & .MuiInput-underline:after': { display: 'none' },
                },
                ...(Array.isArray(sx) ? sx : [sx]),
            ]}
        />
    );
}
